import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import updateAction from '../updateAction';
import { isNumeric } from 'validator';

import { Row, Col } from '../components/Grid';
import Alert from '../components/Alert';
import Animation from '../components/Animation';
import BackButton from '../components/BackButton';
import Button from '../components/Button';
import Input from '../components/Input';
import StackForm from '../components/StackForm';
import Title from '../components/Title';
import Spinner from '../components/Spinner';
import FormError from '../components/FormError';

import videoSinChipFolio from '../../assets/video/ci-sin-chip-folio.mp4';
import videoSinChipSerie from '../../assets/video/ci-sin-chip-serie.mp4';
// import videoSinChip from '../../assets/video/ci-sin-chip.mp4';
import videoConChip from '../../assets/video/ci-con-chip.mp4';
import posterSinChip from '../../assets/video/ci-sin-chip.jpg';
import posterConChip from '../../assets/video/ci-con-chip.jpg';
import { click_next_step3 } from '../techspec';

import { iniciaSolicitudPlatinum } from '../config';
import useStore from '../store';

const Folio = props => {
  const { register, errors, handleSubmit, triggerValidation } = useForm({
    mode: 'onChange',
  });
  const { action, state } = useStateMachine(updateAction);
  const [focusEl, setFocusEl] = useState('serie');

  const isOldCi = state?.data?.tipo_cedula === 'ci-vieja';

  const [cedulaCode, setCedulaCode] = useState('');
  const [invalidCiCode, setInvalidCiCode] = useState('');
  const [prevCiCode, setPrevCiCode] = useState('');

  useEffect(() => {
    setInvalidCiCode('');
  }, [cedulaCode]);

  const [{ selectedGroup, selectedCard, origen }] = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const onSubmit = data => {
    if (prevCiCode === data.cedula_codigo && !isOldCi) return;

    action(data);
    setIsLoading(true);
    setHasError(false);
    setPrevCiCode(data.cedula_codigo);
    setInvalidCiCode('');

    click_next_step3();

    iniciaSolicitudPlatinum(
      Object.assign(state.data, { selectedGroup, selectedCard, origen, ...data }),
    )
      .then(response => {
        if (response && response.status === 201) {
          //devuelve un array de errores, si el length es 0 esta todo bien
          if (response.data.errores.string.length === 0 && response.data.exitoso) {
            action({ solicitudId: response.data.codigoSolicitud });
            setIsLoading(false);
            props.history.push('/4');
          } else {
            setInvalidCiCode(response.data.errores.string[0] || '');
            setHasError(true);
          }
        } else {
          setInvalidCiCode(response.data.errores.string[0] || '');
          setHasError(true);
        }

        setIsLoading(false);
      })
      .catch(error => {
        setHasError(true);
        setIsLoading(false);
      });
    // } else {
    //   action(data);
    //   props.history.push('/4');
    // }
  };

  return (
    <StackForm onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Title>
          {isOldCi
            ? `Necesitamos validar la serie y el folio del documento.`
            : `Necesitamos validar el código de seguridad del documento.`}
        </Title>

        {!isOldCi && (
          <p style={{ color: '#56504C' }}>
            En la parte posterior e inferior de tu cédula encontraras el <b>código de 10 dígitos</b>{' '}
            ubicados después del "URY".
          </p>
        )}

        <div className="reverse-column-md">
          {isOldCi ? (
            <Row>
              <Col size={3}>
                <Input
                  label="Serie"
                  placeholder="T"
                  name="folio_serie"
                  id="folio_serie"
                  mask={{ blocks: [1], uppercase: true }}
                  defaultValue={state?.data?.folio_serie}
                  register={register({
                    required: 'Este campo es obligatorio.',
                    maxLength: { value: 1, message: 'Debe ingresar 1 letra.' },
                    validate: value => (isNumeric(value) ? 'Debe ingresar una letra.' : true),
                  })}
                  error={errors.folio_serie}
                  triggerValidation={() => triggerValidation('folio_serie')}
                  focusOnMount={true}
                  onFocus={() => setFocusEl('serie')}
                />
              </Col>
              <Col size={9}>
                <Input
                  label="Número de Folio"
                  type="tel"
                  name="folio_number"
                  id="folio_number"
                  mask={{ blocks: [6], numericOnly: true }}
                  defaultValue={state?.data?.folio_number}
                  register={register({
                    required: 'Este campo es obligatorio.',
                    maxLength: { value: 6, message: 'Debe ingresar 6 caracteres' },
                    minLength: { value: 6, message: 'Debe ingresar 6 caracteres.' },
                  })}
                  placeholder="123456"
                  error={errors.folio_number}
                  triggerValidation={() => triggerValidation('folio_number')}
                  onFocus={() => setFocusEl('folio')}
                />
              </Col>
            </Row>
          ) : (
            <div>
              <Input
                label="Código de seguridad"
                name="cedula_codigo"
                id="cedula_codigo"
                placeholder="000001ABC2"
                mask={{ blocks: [10], uppercase: true }}
                defaultValue={state?.data?.cedula_codigo}
                error={errors.cedula_codigo}
                onChange={e => setCedulaCode(e.target.value)}
                register={register({
                  required: 'Este campo es obligatorio.',
                  maxLength: { value: 10, message: 'Debe ingresar 10 caracteres' },
                  minLength: { value: 10, message: 'Debe ingresar 10 caracteres.' },
                })}
                triggerValidation={() => triggerValidation('cedula_codigo')}
                focusOnMount={true}
                customError={invalidCiCode && <FormError>{invalidCiCode}</FormError>}
                // customError={<FormError>{invalidCiCode}</FormError>}
              />
            </div>
          )}

          <div>
            {isOldCi ? (
              <AnimatePresence exitBeforeEnter>
                {focusEl === 'serie' ? (
                  <motion.div
                    key="serie"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Animation src={videoSinChipSerie} poster={posterSinChip} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="folio"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Animation src={videoSinChipFolio} poster={posterSinChip} />
                  </motion.div>
                )}
              </AnimatePresence>
            ) : (
              <Animation src={videoConChip} poster={posterConChip} />
            )}
          </div>
        </div>
      </div>

      {hasError && <Alert />}

      <div className="form-actions">
        <Button block disabled={isLoading} type="submit" id="next-button-step-3">
          {isLoading ? <Spinner size={10} /> : 'Siguiente'}
        </Button>

        <BackButton disabled={isLoading} />
      </div>
    </StackForm>
  );
};

export default withRouter(Folio);
